import React, { useState, useEffect } from "react";
import { Check, ChevronDown, Loader2, X } from "lucide-react";
import { DebugField } from "~/components/assistant/messages/DebugField";
import { DebugSection } from "~/components/assistant/messages/DebugSection";
import { ToolCall } from "~/components/assistant/types";
import { cn } from "~/lib/utils";

interface ToolMessageProps {
  toolCall: ToolCall;
  debugMode: boolean;
  expanded?: boolean;
}

export function ToolMessage({ toolCall, debugMode, expanded }: ToolMessageProps) {
  const [isExpanded, setIsExpanded] = useState(expanded ?? false);

  // Sync with controlled expanded prop
  useEffect(() => {
    if (expanded !== undefined) {
      setIsExpanded(expanded);
    }
  }, [expanded]);

  return (
    <div className="border border-gray-200 rounded-lg p-2">
      <details
        className="space-y-3"
        open={isExpanded}
        onToggle={(e) => {
          const target = e.target as HTMLDetailsElement;
          setIsExpanded(target.open);
          e.stopPropagation();
        }}
      >
        <summary className={cn("list-none cursor-pointer mb-0 select-none")}>
          <div className="flex items-center gap-1.5 justify-between">
            <div className="flex gap-2 items-center">
              {toolCall.running ? (
                <Loader2 className="size-3.5 text-purple-600 animate-spin" />
              ) : toolCall.cancelled ? (
                <X className="size-3.5 text-purple-600" />
              ) : (
                <Check className="size-3.5 text-purple-600" />
              )}
              <span className="text-xs font-medium text-purple-600">
                {toolCall.toolName}
                {toolCall.cancelled && " (cancelled)"}
              </span>
            </div>
            <ChevronDown
              className={cn(
                "size-4 text-purple-600 transition-transform duration-200",
                isExpanded && "rotate-180"
              )}
            />
          </div>
        </summary>

        <div className="p-2 mt-2">
          {toolCall.args && Object.keys(toolCall.args).length > 0 && (
            <div>
              {/* Show parsed args content */}
              {toolCall.args.instructions && (
                <>
                  <div className="mb-2">
                    <DebugField label="Instructions" content={toolCall.args.instructions} />
                  </div>
                  {(toolCall.content?.summary || toolCall.content?.raw) && (
                    <div className="mb-2">
                      <DebugField
                        label="Response"
                        content={toolCall.content.summary || toolCall.content.raw || ""}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Debug section for both args and content */}
              {debugMode && (
                <div className="mt-3">
                  <DebugSection
                    content={
                      <div className="space-y-3 mb-1 text-gray-800">
                        <DebugField label="Arguments" content={toolCall.args} />
                        <DebugField label="Content" content={toolCall.content} />
                      </div>
                    }
                    defaultExpanded={false}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </details>
    </div>
  );
}
